import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import api from '@/services/api'
import styles from './ConversationHistoryPage.module.css'
import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns'

interface Conversation {
  id: number
  status: 'active' | 'transferred' | 'closed'
  created_at: string
  updated_at: string
  message_count?: number
  specialist?: {
    id: number
    user: {
      first_name: string
      last_name: string
    }
  } | null
  last_message?: {
    content: string
    sender_type: string
    created_at: string
  } | null
}

type FilterStatus = 'all' | 'active' | 'transferred' | 'closed'

export default function ConversationHistoryPage() {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all')
  const navigate = useNavigate()
  const { user: _user } = useAuth()

  // Fetch conversations
  useEffect(() => {
    fetchConversations()
  }, [])

  const fetchConversations = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get('/conversations/')
      setConversations(response.data.results || response.data || [])
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load conversations')
    } finally {
      setLoading(false)
    }
  }

  // Filter and search conversations
  const filteredConversations = useMemo(() => {
    let result = [...conversations]

    // Apply status filter
    if (filterStatus !== 'all') {
      result = result.filter(conv => conv.status === filterStatus)
    }

    // Apply search query (search in last message if available)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter(conv => {
        const lastMessageContent = conv.last_message?.content?.toLowerCase() || ''
        const specialistName = conv.specialist 
          ? `${conv.specialist.user.first_name} ${conv.specialist.user.last_name}`.toLowerCase()
          : ''
        return lastMessageContent.includes(query) || specialistName.includes(query)
      })
    }

    // Sort by updated_at (most recent first)
    result.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())

    return result
  }, [conversations, filterStatus, searchQuery])

  // Format timestamp for display
  const formatTimestamp = (dateString: string) => {
    const date = new Date(dateString)
    if (isToday(date)) {
      return formatDistanceToNow(date, { addSuffix: true })
    }
    if (isYesterday(date)) {
      return 'Yesterday'
    }
    return format(date, 'MMM d, yyyy')
  }

  // Get status badge config
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return { label: 'Active', className: styles.statusActive, dotClass: styles.dotActive }
      case 'transferred':
        return { label: 'With Specialist', className: styles.statusTransferred, dotClass: styles.dotTransferred }
      case 'closed':
        return { label: 'Closed', className: styles.statusClosed, dotClass: styles.dotClosed }
      default:
        return { label: status, className: styles.statusActive, dotClass: styles.dotActive }
    }
  }

  // Get preview text for conversation
  const getPreviewText = (conv: Conversation) => {
    if (conv.last_message?.content) {
      const senderPrefix = conv.last_message.sender_type === 'specialist' 
        ? `Dr. ${conv.specialist?.user.last_name || 'Specialist'}: `
        : conv.last_message.sender_type === 'ai'
          ? 'AI: '
          : ''
      return `${senderPrefix}${conv.last_message.content}`
    }
    return 'Start your conversation...'
  }

  // Handle conversation click
  const handleConversationClick = (conversationId: number) => {
    navigate(`/chat/${conversationId}`)
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading your conversations...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Link to="/dashboard" className={styles.backButton}>
          ← Back to Dashboard
        </Link>
        <h1 className={styles.title}>Conversation History</h1>
      </div>

      <div className={styles.content}>
        {/* Search and Filter Controls */}
        <div className={styles.controls}>
          <div className={styles.searchContainer}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <div className={styles.filterContainer}>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
              className={styles.filterSelect}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="transferred">With Specialist</option>
              <option value="closed">Closed</option>
            </select>
            <span className={styles.filterArrow}>▼</span>
          </div>
        </div>

        {error ? (
          <div className={styles.error}>
            <p>{error}</p>
            <button onClick={fetchConversations} className={styles.retryButton}>
              Try Again
            </button>
          </div>
        ) : conversations.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>💬</div>
            <h2 className={styles.emptyTitle}>No Conversations Yet</h2>
            <p className={styles.emptyDescription}>
              Start your first conversation with our AI mental health assistant
            </p>
            <Link to="/chat" className={styles.startButton}>
              🚀 Start New Conversation
            </Link>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className={styles.noResults}>
            <div className={styles.noResultsIcon}>🔍</div>
            <p>No conversations match your search</p>
          </div>
        ) : (
          <div className={styles.conversationsList}>
            {filteredConversations.map((conv) => {
              const statusConfig = getStatusConfig(conv.status)
              return (
                <div
                  key={conv.id}
                  className={styles.conversationCard}
                  onClick={() => handleConversationClick(conv.id)}
                >
                  <div className={styles.cardHeader}>
                    <span className={`${styles.statusBadge} ${statusConfig.className}`}>
                      <span className={`${styles.statusDot} ${statusConfig.dotClass}`}></span>
                      {statusConfig.label}
                    </span>
                    <span className={styles.timestamp}>
                      {formatTimestamp(conv.updated_at)}
                    </span>
                  </div>
                  <p className={styles.preview}>
                    "{getPreviewText(conv)}"
                  </p>
                  <div className={styles.cardFooter}>
                    <span className={styles.messageCount}>
                      💬 {conv.message_count || 0} messages
                    </span>
                    {conv.specialist && (
                      <span className={styles.specialistName}>
                        • Dr. {conv.specialist.user.first_name} {conv.specialist.user.last_name}
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
